import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FileText, Download, Shield, CheckCircle, Clock, Ban,
  Loader2, Building2, CreditCard, Banknote, Landmark, Copy, Check
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  draft: { color: 'bg-slate-100 text-slate-600', label: 'Draft / Borrador' },
  issued: { color: 'bg-blue-100 text-blue-700', label: 'Issued / Emitida', icon: Clock },
  paid: { color: 'bg-green-100 text-green-700', label: 'Paid / Pagada', icon: CheckCircle },
  cancelled: { color: 'bg-red-100 text-red-600', label: 'Cancelled / Anulada', icon: Ban },
};

const PublicInvoicePage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        const res = await axios.get(`${API}/api/invoices/public/${token}`);
        setData(res.data);
      } catch (e) {
        setError(e.response?.status === 404 ? 'not_found' : 'error');
      } finally {
        setLoading(false);
      }
    };
    fetchInvoice();
  }, [token]);

  const handleDownloadPDF = async () => {
    try {
      const res = await axios.get(`${API}/api/invoices/public/${token}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${data.invoice.invoice_number}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Error downloading PDF');
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1e3a5f]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
          <h1 className="text-2xl font-bold text-[#1e3a5f] mb-2">
            {error === 'not_found' ? 'Invoice Not Found' : 'Error Loading Invoice'}
          </h1>
          <p className="text-slate-500">
            {error === 'not_found'
              ? 'This invoice link is invalid or has expired.'
              : 'Something went wrong. Please try again later.'}
          </p>
        </div>
      </div>
    );
  }

  const { invoice, shop, payment_options } = data;
  const statusCfg = STATUS_CONFIG[invoice.status] || STATUS_CONFIG.draft;
  const StatusIcon = statusCfg.icon;

  const PAYMENT_ICONS = {
    bank_transfer: Landmark,
    cash: Banknote,
    card: CreditCard,
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]" data-testid="public-invoice-page">
      {/* Header Bar */}
      <header className="bg-[#1e3a5f] text-white">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-white/10 rounded-sm flex items-center justify-center">
              <FileText className="w-5 h-5" />
            </div>
            <div>
              <h1 className="font-bold text-lg tracking-tight">VendFactura</h1>
              <p className="text-white/50 text-xs">VeriFactu Compliant Invoice</p>
            </div>
          </div>
          <button
            onClick={handleDownloadPDF}
            className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-sm text-sm font-medium transition-colors"
            data-testid="public-download-pdf"
          >
            <Download className="w-4 h-4" />
            PDF
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        {/* Status + Number */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border-2 border-slate-100 p-5"
        >
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Invoice</p>
              <h2 className="text-2xl font-bold text-[#1e3a5f] font-mono" data-testid="public-invoice-number">
                {invoice.invoice_number}
              </h2>
              <p className="text-slate-500 text-sm mt-1">Date: {invoice.issue_date}</p>
              {invoice.due_date && (
                <p className="text-slate-500 text-sm">Due: {invoice.due_date}</p>
              )}
            </div>
            <div className="text-right">
              <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-sm font-medium ${statusCfg.color}`}>
                {StatusIcon && <StatusIcon className="w-4 h-4" />}
                {statusCfg.label}
              </span>
              <div className="mt-3">
                <p className="text-xs text-slate-400">Total</p>
                <p className="text-3xl font-bold text-[#22c55e]" data-testid="public-invoice-total">
                  {invoice.total.toFixed(2)} EUR
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Issuer & Customer */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="grid grid-cols-1 sm:grid-cols-2 gap-4"
        >
          <div className="bg-white border-2 border-slate-100 p-4">
            <div className="flex items-center gap-2 mb-3">
              <Building2 className="w-4 h-4 text-[#1e3a5f]" />
              <p className="text-xs font-semibold text-[#1e3a5f] uppercase tracking-wider">From / De</p>
            </div>
            <p className="font-semibold text-[#1e3a5f]">{invoice.issuer?.name}</p>
            {invoice.issuer?.nif && <p className="text-sm text-slate-500">NIF: {invoice.issuer.nif}</p>}
            {invoice.issuer?.address && <p className="text-sm text-slate-500">{invoice.issuer.address}</p>}
            {invoice.issuer?.city && <p className="text-sm text-slate-500">{invoice.issuer.city} {invoice.issuer?.postal_code}</p>}
            {shop?.phone && <p className="text-sm text-slate-500">Tel: {shop.phone}</p>}
            {shop?.email && <p className="text-sm text-slate-500">{shop.email}</p>}
          </div>
          <div className="bg-white border-2 border-slate-100 p-4">
            <p className="text-xs font-semibold text-[#1e3a5f] uppercase tracking-wider mb-3">To / Para</p>
            <p className="font-semibold text-[#1e3a5f]">{invoice.customer?.name}</p>
            {invoice.customer?.nif && <p className="text-sm text-slate-500">NIF: {invoice.customer.nif}</p>}
            {invoice.customer?.address && <p className="text-sm text-slate-500">{invoice.customer.address}</p>}
            {invoice.customer?.city && <p className="text-sm text-slate-500">{invoice.customer.city} {invoice.customer?.postal_code}</p>}
            {invoice.customer?.email && <p className="text-sm text-slate-500">{invoice.customer.email}</p>}
          </div>
        </motion.div>

        {/* Items Table */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white border-2 border-slate-100 overflow-hidden"
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="public-invoice-items">
              <thead>
                <tr className="border-b-2 border-slate-100 bg-slate-50">
                  <th className="px-4 py-3 text-left font-semibold text-[#1e3a5f]">Description</th>
                  <th className="px-4 py-3 text-center font-semibold text-[#1e3a5f]">Qty</th>
                  <th className="px-4 py-3 text-right font-semibold text-[#1e3a5f]">Price</th>
                  <th className="px-4 py-3 text-center font-semibold text-[#1e3a5f]">IVA</th>
                  <th className="px-4 py-3 text-right font-semibold text-[#1e3a5f]">Total</th>
                </tr>
              </thead>
              <tbody>
                {invoice.items?.map((item, i) => {
                  const lineTotal = item.quantity * item.price * (1 + item.tax_rate / 100);
                  return (
                    <tr key={i} className="border-b border-slate-50">
                      <td className="px-4 py-3">{item.description}</td>
                      <td className="px-4 py-3 text-center">{item.quantity}</td>
                      <td className="px-4 py-3 text-right">{item.price.toFixed(2)}</td>
                      <td className="px-4 py-3 text-center">{item.tax_rate}%</td>
                      <td className="px-4 py-3 text-right font-medium">{lineTotal.toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="border-t-2 border-slate-100 p-4 bg-slate-50">
            <div className="max-w-xs ml-auto space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Subtotal</span>
                <span>{invoice.subtotal?.toFixed(2)} EUR</span>
              </div>
              {invoice.tax_breakdown?.map((tb, i) => (
                <div key={i} className="flex justify-between text-slate-500">
                  <span>IVA {tb.rate}%</span>
                  <span>{tb.amount.toFixed(2)} EUR</span>
                </div>
              ))}
              <div className="flex justify-between font-bold text-lg text-[#1e3a5f] pt-2 border-t border-slate-200">
                <span>TOTAL</span>
                <span className="text-[#22c55e]">{invoice.total?.toFixed(2)} EUR</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Payment Options */}
        {payment_options && payment_options.length > 0 && invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-white border-2 border-slate-100 p-5"
            data-testid="payment-options-section"
          >
            <h3 className="font-bold text-[#1e3a5f] mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Payment Options / Opciones de Pago
            </h3>
            <div className="space-y-4">
              {payment_options.filter(m => m.enabled).map((method, i) => {
                const Icon = PAYMENT_ICONS[method.type] || CreditCard;
                return (
                  <div key={i} className="border border-slate-200 rounded-sm p-4" data-testid={`payment-option-${method.type}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className="w-4 h-4 text-[#1e3a5f]" />
                      <span className="font-semibold text-[#1e3a5f]">
                        {method.label || method.type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                    </div>

                    {method.type === 'bank_transfer' && (
                      <div className="bg-slate-50 p-3 rounded-sm space-y-2 text-sm">
                        {method.bank_name && (
                          <div className="flex justify-between">
                            <span className="text-slate-500">Bank</span>
                            <span className="font-medium text-[#1e3a5f]">{method.bank_name}</span>
                          </div>
                        )}
                        {method.account_holder && (
                          <div className="flex justify-between">
                            <span className="text-slate-500">Account Holder</span>
                            <span className="font-medium text-[#1e3a5f]">{method.account_holder}</span>
                          </div>
                        )}
                        {method.iban && (
                          <div className="flex items-center justify-between">
                            <span className="text-slate-500">IBAN</span>
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-medium text-[#1e3a5f]">{method.iban}</span>
                              <button
                                onClick={() => copyToClipboard(method.iban, `iban-${i}`)}
                                className="p-1 hover:bg-slate-200 rounded-sm"
                                data-testid={`copy-iban-${i}`}
                              >
                                {copiedField === `iban-${i}` ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5 text-slate-400" />}
                              </button>
                            </div>
                          </div>
                        )}
                        {method.swift_bic && (
                          <div className="flex items-center justify-between">
                            <span className="text-slate-500">SWIFT/BIC</span>
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-medium text-[#1e3a5f]">{method.swift_bic}</span>
                              <button
                                onClick={() => copyToClipboard(method.swift_bic, `swift-${i}`)}
                                className="p-1 hover:bg-slate-200 rounded-sm"
                              >
                                {copiedField === `swift-${i}` ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5 text-slate-400" />}
                              </button>
                            </div>
                          </div>
                        )}
                        {method.sort_code && (
                          <div className="flex justify-between">
                            <span className="text-slate-500">Sort Code</span>
                            <span className="font-mono font-medium text-[#1e3a5f]">{method.sort_code}</span>
                          </div>
                        )}
                        {method.account_number && (
                          <div className="flex justify-between">
                            <span className="text-slate-500">Account No.</span>
                            <span className="font-mono font-medium text-[#1e3a5f]">{method.account_number}</span>
                          </div>
                        )}
                        <div className="pt-1 text-xs text-slate-400">
                          Reference: {invoice.invoice_number}
                        </div>
                      </div>
                    )}

                    {method.type === 'cash' && method.instructions && (
                      <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-sm">{method.instructions}</p>
                    )}

                    {method.type === 'card' && method.instructions && (
                      <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-sm">{method.instructions}</p>
                    )}

                    {method.type === 'other' && method.instructions && (
                      <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-sm">{method.instructions}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Paid Badge */}
        {invoice.status === 'paid' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-green-50 border-2 border-green-200 p-6 text-center"
          >
            <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-600" />
            <h3 className="text-xl font-bold text-green-700">Invoice Paid / Factura Pagada</h3>
          </motion.div>
        )}

        {/* VeriFactu Footer */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-[#1e3a5f]/5 border border-[#1e3a5f]/15 p-4 rounded-sm"
        >
          <div className="flex items-center gap-2 mb-2">
            <Shield className="w-4 h-4 text-[#22c55e]" />
            <span className="text-sm font-semibold text-[#1e3a5f]">VeriFactu Compliant</span>
          </div>
          <p className="text-xs text-slate-500 font-mono break-all">
            Hash: {invoice.verifactu_hash}
          </p>
          <p className="text-xs text-slate-400 mt-1">
            Factura generada conforme al Reglamento VeriFactu (RD 1007/2023)
          </p>
        </motion.div>

        {/* Notes */}
        {invoice.notes && (
          <div className="bg-white border border-slate-200 p-4 text-sm text-slate-600 italic">
            {invoice.notes}
          </div>
        )}

        <div className="text-center py-4 text-xs text-slate-400">
          Powered by VendFactura &middot; vendfactura.es
        </div>
      </main>
    </div>
  );
};

export default PublicInvoicePage;
